import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useMemo, useImperativeHandle, cloneElement } from "react";
import PropTypes from "prop-types";
import { min, map } from "lodash/fp";
import { useRect } from "@reach/rect";
import { Portal } from "@reach/portal";
import { useTooltip } from "@reach/tooltip";
import { composeEventHandlers } from "@reach/utils";
import { AnimatePresence, motion } from "framer-motion";
import { easeQuadOut } from "d3-ease";
import { TooltipBox, TooltipSupplementary } from "./tooltip.styles.es.js";
import { adjustPlacement } from "../../utils/adjust-placement.es.js";
import { usePopoverPosition } from "../../hooks/use-popover-position.es.js";
const propTypes = {
  "aria-label": PropTypes.string,
  buffer: PropTypes.number,
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool,
  disabled: PropTypes.bool,
  justify: PropTypes.oneOf(["start", "end", "center"]),
  maxWidth: PropTypes.number,
  offset: PropTypes.number,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseMove: PropTypes.func,
  placement: PropTypes.oneOf(["bottom", "left", "right", "top"]),
  showTriangle: PropTypes.bool,
  subtitle: PropTypes.string,
  title: PropTypes.node
};
const defaultProps = {
  "aria-label": void 0,
  buffer: 16,
  defaultExpanded: false,
  disabled: false,
  justify: "center",
  maxWidth: void 0,
  offset: 4,
  onBlur: void 0,
  onFocus: void 0,
  onKeyDown: void 0,
  onMouseDown: void 0,
  onMouseEnter: void 0,
  onMouseLeave: void 0,
  onMouseMove: void 0,
  placement: "bottom",
  showTriangle: true,
  subtitle: void 0,
  title: void 0
};
const Tooltip = forwardRef((props, forwardedRef) => {
  const {
    "aria-label": ariaLabel,
    buffer,
    children,
    defaultExpanded,
    disabled,
    justify,
    maxWidth,
    offset,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onMouseMove,
    placement,
    showTriangle,
    subtitle,
    title,
    ...restProps
  } = props;
  const ref = useRef();
  const tooltipRef = useRef();
  const tooltipRect = useRect(tooltipRef, { observe: true });
  const [trigger, { isVisible, triggerRect, ...tooltipProps }] = useTooltip({
    onBlur: onBlur ? composeEventHandlers(children.props.onBlur, onBlur) : children.props.onBlur,
    onFocus: onFocus ? composeEventHandlers(children.props.onFocus, onFocus) : children.props.onFocus,
    onKeyDown: onKeyDown ? composeEventHandlers(children.props.onKeyDown, onKeyDown) : children.props.onKeyDown,
    onMouseDown: onMouseDown ? composeEventHandlers(children.props.onMouseDown, onMouseDown) : children.props.onMouseDown,
    onMouseEnter: onMouseEnter ? composeEventHandlers(children.props.onMouseEnter, onMouseEnter) : children.props.onMouseEnter,
    onMouseLeave: onMouseLeave ? composeEventHandlers(children.props.onMouseLeave, onMouseLeave) : children.props.onMouseLeave,
    onMouseMove: onMouseMove ? composeEventHandlers(children.props.onMouseMove, onMouseMove) : children.props.onMouseMove,
    ref,
    DEBUG_STYLE: defaultExpanded
  });
  const popoverPosition = usePopoverPosition({
    placement,
    justify,
    offset,
    centerWhenSmall: true
  });
  const [dimension, scaleFunctionName] = placement === "left" || placement === "right" ? ["height", "scaleX"] : ["width", "scaleY"];
  const calculatePlacement = useMemo(
    () => adjustPlacement(placement, buffer),
    [buffer, placement]
  );
  const targetSize = min(map(dimension, [triggerRect, tooltipRect]));
  useImperativeHandle(forwardedRef, () => ref.current);
  useImperativeHandle(children.ref, () => ref.current);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    cloneElement(children, { ...restProps, ...trigger }),
    !disabled && /* @__PURE__ */ jsx(AnimatePresence, { children: isVisible && /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(
      "div",
      {
        ref: tooltipRef,
        role: "tooltip",
        "data-reach-tooltip": "",
        style: tooltipRect ? {
          ...popoverPosition(triggerRect, tooltipRect),
          position: "absolute"
        } : {
          visibility: "hidden"
        },
        children: /* @__PURE__ */ jsx(
          motion.div,
          {
            ...tooltipProps,
            "aria-label": ariaLabel,
            style: {
              transformOrigin: "inherit"
            },
            initial: {
              opacity: 0,
              transform: `${scaleFunctionName}(0.8)`
            },
            animate: {
              opacity: 1,
              transform: `${scaleFunctionName}(1)`
            },
            exit: {
              opacity: 0,
              transform: `${scaleFunctionName}(0.8)`
            },
            transition: {
              ease: easeQuadOut,
              delay: 0.05,
              duration: 0.1
            },
            children: /* @__PURE__ */ jsxs(
              TooltipBox,
              {
                $justify: justify,
                $maxWidth: maxWidth,
                $placement: calculatePlacement(triggerRect, tooltipRect),
                $showTriangle: showTriangle,
                $targetSize: targetSize,
                children: [
                  title,
                  subtitle && /* @__PURE__ */ jsx(TooltipSupplementary, { children: subtitle })
                ]
              }
            )
          }
        )
      }
    ) }) })
  ] });
});
Tooltip.propTypes = propTypes;
Tooltip.defaultProps = defaultProps;
export {
  Tooltip
};
